import { useState, type ReactNode } from "react";

const KEY_PREFIX = "anansi.sidebar.";

/** This browser's remembered state for a group, or null when there is none.
 *  Reads are wrapped: private windows and locked-down profiles throw here. */
function readStored(id: string): boolean | null {
  try {
    const v = localStorage.getItem(`${KEY_PREFIX}${id}`);
    return v === null ? null : v === "open";
  } catch {
    return null;
  }
}

/** Remember a group's state for this browser; failure costs only the memory. */
function writeStored(id: string, open: boolean) {
  try {
    localStorage.setItem(`${KEY_PREFIX}${id}`, open ? "open" : "closed");
  } catch {
    // Best effort — the group still toggles for this page view.
  }
}

/**
 * A titled sidebar section (spec §5.6), collapsible or not.
 *
 * A collapsible group remembers open or closed per browser under
 * `anansi.sidebar.<id>`. `forceOpen` overrides both the memory and the toggle
 * — set it when a child route is active, so the nav can never hide the page
 * the operator is currently on.
 */
export function SidebarGroup({
  id,
  label,
  collapsible = false,
  defaultOpen = true,
  forceOpen = false,
  children,
}: {
  id: string;
  label: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** Show the children regardless of stored state — set when a child route is active. */
  forceOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState<boolean>(() => (collapsible ? (readStored(id) ?? defaultOpen) : true));
  const showChildren = !collapsible || open || forceOpen;

  /** Open or close the group, remembering the choice for the next visit. A group
   *  held open because it contains the current route does not collapse. */
  function toggle() {
    if (forceOpen) return;
    const next = !open;
    setOpen(next);
    writeStored(id, next);
  }

  return (
    <div className="sidebar__group">
      <div className="sidebar__section">
        {collapsible ? (
          <button
            type="button"
            className="sidebar__group-toggle uppercase-label"
            aria-expanded={open || forceOpen}
            disabled={forceOpen}
            onClick={toggle}
          >
            <span>{label}</span>
            <span className={`sidebar__chevron${open || forceOpen ? " sidebar__chevron--open" : ""}`} aria-hidden="true">
              ▸
            </span>
          </button>
        ) : (
          <span className="uppercase-label">{label}</span>
        )}
      </div>
      {showChildren ? children : null}
    </div>
  );
}
